import {
  clearDeliveryConfirmationsForRelease,
  seedDeliveryConfirmationsForRelease,
} from "./deliveryConfirmationDemoHelpers";
import { resolveDemoReleaseStoreUrls } from "./demoReleaseStoreUrlHelpers";

const TRANSITIONS_KEY = "wl_bs_release_status_transitions";
const TO_DELIVERING_MS = 30000;
const TO_DISTRIBUTED_MS = 10000;
const TO_TAKEN_DOWN_MS = 12000;

const activeTimers = new Map();
let transitionPollIntervalId = null;

function readTransitions() {
  if (typeof sessionStorage === "undefined") {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(TRANSITIONS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeTransitions(transitions) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(TRANSITIONS_KEY, JSON.stringify(transitions || {}));
}

export function notifyReleaseStatusUpdated() {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new CustomEvent("wl-demo-release-status-updated"));
}

function clearReleaseTransition(releaseId) {
  const sid = String(releaseId);
  const transitions = readTransitions();
  if (!transitions[sid]) {
    return;
  }
  delete transitions[sid];
  writeTransitions(transitions);
  if (activeTimers.has(sid)) {
    clearTimeout(activeTimers.get(sid));
    activeTimers.delete(sid);
  }
}

function armTransitionTimer(releaseId, patchReleaseRecord) {
  if (typeof window === "undefined" || typeof patchReleaseRecord !== "function") {
    return;
  }

  const sid = String(releaseId);
  const transitions = readTransitions();
  const entry = transitions[sid];
  if (!entry) {
    return;
  }

  if (activeTimers.has(sid)) {
    clearTimeout(activeTimers.get(sid));
  }

  const delay = Math.max(0, Number(entry.dueAt) - Date.now());
  const timerId = window.setTimeout(() => {
    activeTimers.delete(sid);
    applyDueReleaseStatusTransitions(patchReleaseRecord);
  }, delay);

  activeTimers.set(sid, timerId);
}

function ensureTransitionPoll(patchReleaseRecord) {
  if (typeof window === "undefined" || transitionPollIntervalId != null) {
    return;
  }
  transitionPollIntervalId = window.setInterval(() => {
    applyDueReleaseStatusTransitions(patchReleaseRecord);
  }, 2000);
}

/** Maps distribute / takedown / submit-editing actions to demo-friendly persisted release statuses. */
export function normalizeDemoReleaseStatusPatch(releaseId, body = {}) {
  if (body.status == null) {
    return body;
  }

  const patch = { ...body };

  if (patch.status === "taken_down") {
    patch.has_delivery_confirmations = false;
    patch.store_urls = [];
    clearDeliveryConfirmationsForRelease(releaseId);
    return patch;
  }

  return patch;
}

export function enrichReleaseListRowFromStoredDetail(listRow, detailsById) {
  if (!listRow || listRow.id == null) {
    return listRow;
  }
  const detail = detailsById[String(listRow.id)];
  if (!detail) {
    return {
      ...listRow,
      store_urls: resolveDemoReleaseStoreUrls(listRow),
    };
  }

  const merged = {
    ...listRow,
    ...(detail.kind != null ? { kind: detail.kind } : {}),
    ...(detail.status != null ? { status: detail.status } : {}),
    ...(detail.all_tracks_ready != null ? { all_tracks_ready: detail.all_tracks_ready } : {}),
    ...(detail.has_delivery_confirmations != null
      ? { has_delivery_confirmations: detail.has_delivery_confirmations }
      : {}),
    ...(detail.pre_scan != null ? { pre_scan: detail.pre_scan } : {}),
    ...(detail.pre_scan_done != null ? { pre_scan_done: detail.pre_scan_done } : {}),
    ...(detail.release_pre_scan_declaration != null
      ? { release_pre_scan_declaration: detail.release_pre_scan_declaration }
      : {}),
    ...(detail.estimated_delivery_time != null
      ? { estimated_delivery_time: detail.estimated_delivery_time }
      : {}),
    ...(Array.isArray(detail.stores) ? { stores: detail.stores } : {}),
  };

  return {
    ...merged,
    store_urls: resolveDemoReleaseStoreUrls({ ...detail, ...merged }),
  };
}

export function scheduleReleaseDeliveryProgression(releaseId, status, patchReleaseRecord) {
  if (status !== "approval" && status !== "re_delivery-delivery") {
    return;
  }

  const sid = String(releaseId);
  const transitions = readTransitions();
  transitions[sid] = {
    phase: "to_delivering",
    dueAt: Date.now() + TO_DELIVERING_MS,
  };
  writeTransitions(transitions);
  ensureTransitionPoll(patchReleaseRecord);
  armTransitionTimer(sid, patchReleaseRecord);
}

export function scheduleReleaseTakedownProgression(releaseId, patchReleaseRecord) {
  const sid = String(releaseId);
  const transitions = readTransitions();
  transitions[sid] = {
    phase: "to_taken_down",
    dueAt: Date.now() + TO_TAKEN_DOWN_MS,
  };
  writeTransitions(transitions);
  ensureTransitionPoll(patchReleaseRecord);
  armTransitionTimer(sid, patchReleaseRecord);
}

export function applyDueReleaseStatusTransitions(patchReleaseRecord) {
  if (typeof patchReleaseRecord !== "function") {
    return false;
  }

  const transitions = readTransitions();
  const now = Date.now();
  let changed = false;

  Object.keys(transitions).forEach(releaseId => {
    const entry = transitions[releaseId];
    if (!entry || now < Number(entry.dueAt)) {
      return;
    }

    if (entry.phase === "to_delivering") {
      patchReleaseRecord(releaseId, { status: "delivering" });
      transitions[releaseId] = {
        phase: "to_distributed",
        dueAt: now + TO_DISTRIBUTED_MS,
      };
      changed = true;
      return;
    }

    if (entry.phase === "to_distributed") {
      patchReleaseRecord(releaseId, {
        status: "distributed",
        has_delivery_confirmations: true,
      });
      delete transitions[releaseId];
      changed = true;
      return;
    }

    if (entry.phase === "to_taken_down") {
      patchReleaseRecord(releaseId, { status: "taken_down" });
      delete transitions[releaseId];
      changed = true;
    }
  });

  writeTransitions(transitions);

  if (changed) {
    notifyReleaseStatusUpdated();
    Object.keys(transitions).forEach(releaseId => {
      armTransitionTimer(releaseId, patchReleaseRecord);
    });
  }

  return changed;
}

export function bootstrapReleaseTransitionTimers(patchReleaseRecord) {
  if (typeof patchReleaseRecord !== "function") {
    return;
  }
  const transitions = readTransitions();
  if (!Object.keys(transitions).length) {
    return;
  }
  ensureTransitionPoll(patchReleaseRecord);
  Object.keys(transitions).forEach(releaseId => {
    armTransitionTimer(releaseId, patchReleaseRecord);
  });
}

export function afterDemoReleaseStatusPatch(releaseId, status, patchReleaseRecord) {
  if (status === "approval" || status === "re_delivery-delivery") {
    scheduleReleaseDeliveryProgression(releaseId, status, patchReleaseRecord);
    return;
  }
  if (status === "takedown_request") {
    scheduleReleaseTakedownProgression(releaseId, patchReleaseRecord);
    return;
  }
  if (status === "distributed") {
    clearReleaseTransition(releaseId);
    seedDeliveryConfirmationsForRelease(releaseId);
    return;
  }
  if (status === "taken_down" || status === "offline") {
    clearReleaseTransition(releaseId);
  }
}
